/**
 * reconcileGameImport — turns parsed import sources into Firestore-ready output.
 *
 * Takes the scorebook (and, when available, the Game Stats Sheet PDF / season CSV)
 * and produces: AtBatDoc[] (both teams, us/opp resolved + roster-matched), per-game
 * stat rollups for our batters, derived pitch counts for our pitchers, and an
 * opponent record for scouting accrual.
 *
 * Source precedence (the "three sources collaborate" rule): explicit stat lines
 * (CSV / Stats-Sheet PDF) win for totals; the scorebook wins for pitch sequences,
 * inning timing and lineup-slot context. Game date is the sync key for filing to a
 * game. Pure — no Firestore; the App layer writes the result.
 */

import { PlayerStats, matchPlayers } from '../xovr-engine';
import { ParsedGCPlayer } from '../gcCsvParser';
import { AtBatDoc, OpponentRecord } from '../../types/dugout';
import { ParsedScorebook, ParsedTeamPage, ParsedAtBat, ParsedGameStatsSheet, StatsSheetPitcher, PageSide } from '../../types/import.types';

export interface RosterRef { id: string; name: string; number: string }

/** Rate stats only GameChanger computes, carried by the CSV export. Every field
 *  is optional: a rate GC could not compute (no qualifying batters) comes across
 *  as "-" and must stay absent rather than becoming 0. */
export interface PitcherRates {
  fpsPct?: number;    // first-pitch strike %
  fpsoPct?: number;   // of first-pitch-strike ABs, % that ended in an out
  fpswPct?: number;   // ...that ended in a walk
  fpshPct?: number;   // ...that ended in a hit
  smPct?: number;     // swinging-miss % of total pitches
  kBf?: number;       // strikeouts per batter faced
  loo?: number;       // leadoff outs
  lowPitchInn?: number; // innings of 13 pitches or fewer
  weakPct?: number;   // % of batted balls weakly hit
  goAo?: number;      // ground-out / air-out
  baa?: number;       // opponent batting average
}

export interface ReconcileInput {
  scorebook?: ParsedScorebook;
  gameStatsSheet?: ParsedGameStatsSheet;
  /** The GameChanger CSV export, filtered to this one game. Third source in the
   *  triangulation: it is the ONLY one carrying rate stats (first-pitch strike,
   *  swinging-miss, two-strike outcomes, batted-ball quality). The scorebook has
   *  play-by-play, the stats sheet has exact totals, the CSV has the rates. */
  gameCsv?: ParsedGCPlayer[];
  coachTeamName: string;
  roster: RosterRef[];
  /** Manual player mappings from the import UI, keyed `${number}::${name}` → playerId. */
  overrides?: Record<string, string>;
}

export interface ReconciledGame {
  date?: string;
  opponent?: string;
  opponentKey?: string;
  ourTeamName?: string;
  homeOrAway?: PageSide;
  atBats: AtBatDoc[];                          // gameId filled in by the persistence layer
  playerGameStats: Record<string, PlayerStats>; // our matched batters → per-game line
  pitchCounts: Record<string, number>;          // our matched pitchers → pitch count
  /** Exact per-pitcher line from the Game Stats Sheet (when one was uploaded),
   *  keyed by our matched player id. This is GameChanger's own computed BF/P-S/
   *  H/BB/SO — the ground truth the scorebook's cell-by-cell grid parse is only
   *  ever estimating. Downstream signal generation (game-signals.ts) should
   *  prefer these over anything it re-derives from individual at-bats. */
  pitcherLines: Record<string, StatsSheetPitcher>;
  /** Per-pitcher rate stats from the game-filtered CSV, keyed by our player id.
   *  Only populated when the CSV's per-pitcher IP/BF/pitches agree with the box
   *  score, i.e. it really is this game and not a season-to-date export. */
  pitcherRates: Record<string, PitcherRates>;
  opponentRecord: OpponentRecord | null;
  unmatched: Array<{ name: string; number: string; role: 'batter' | 'pitcher' }>;
  warnings: string[];
}

const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const splitName = (full: string) => {
  const parts = (full || '').trim().split(/\s+/);
  return parts.length <= 1 ? { first: '', last: parts[0] || '' } : { first: parts[0], last: parts[parts.length - 1] };
};
const opponentKeyOf = (name: string) => norm(name);

/** Pick the page that is the coach's team; the other is the opponent. */
function identifyOurPage(book: ParsedScorebook, coachTeamName: string, roster: RosterRef[], warnings: string[]): {
  our?: ParsedTeamPage; opp?: ParsedTeamPage;
} {
  if (book.pages.length < 2) {
    warnings.push('Scorebook has fewer than two team pages, so could not separate us from opponent.');
    return { our: book.pages[0], opp: undefined };
  }
  const target = norm(coachTeamName);
  let our = book.pages.find(p => { const n = norm(p.teamName); return target && (n.includes(target) || target.includes(n)); });

  if (!our) {
    // Fallback: the page whose roster jersey numbers best overlap ours.
    const rosterNums = new Set(roster.map(r => String(r.number).trim()));
    let best = -1;
    for (const p of book.pages) {
      const overlap = p.lineup.filter(l => rosterNums.has(String(l.number).trim())).length;
      if (overlap > best) { best = overlap; our = p; }
    }
    warnings.push(`Team name "${coachTeamName}" didn't match a page directly, matched by roster jersey overlap (${best}).`);
  }
  const opp = book.pages.find(p => p !== our);
  return { our, opp };
}

/** Resolve scorebook people (name+number) to roster playerIds via matchPlayers,
 *  with manual overrides (from the import UI) taking precedence. */
function resolveToRoster(people: Array<{ name: string; number: string }>, roster: RosterRef[], overrides?: Record<string, string>): Map<string, RosterRef> {
  const rows = people.map(p => ({ ...splitName(p.name), number: p.number, stats: null }));
  const results = matchPlayers(rows, roster.map(r => ({ id: r.id, name: r.name, number: r.number })));
  const map = new Map<string, RosterRef>();
  results.forEach((res, i) => {
    if (res.matchedPlayerId) {
      map.set(keyOf(people[i].number, people[i].name), { id: res.matchedPlayerId, name: res.matchedPlayerName!, number: people[i].number });
    }
  });
  // Manual overrides win over (and fill gaps in) the auto-match.
  if (overrides) {
    for (const p of people) {
      const pid = overrides[`${p.number}::${p.name}`];
      const rp = pid && roster.find(r => r.id === pid);
      if (rp) map.set(keyOf(p.number, p.name), { id: rp.id, name: rp.name, number: p.number });
    }
  }
  return map;
}

const keyOf = (number: string, name: string) => `${String(number).trim()}|${norm(name)}`;

function uniquePeople(atBats: ParsedAtBat[], pick: 'batter' | 'pitcher'): Array<{ name: string; number: string }> {
  const seen = new Map<string, { name: string; number: string }>();
  for (const ab of atBats) {
    const name = pick === 'batter' ? ab.batterName : (ab.pitcherName || '');
    const number = pick === 'batter' ? ab.batterNumber : (ab.pitcherNumber || '');
    if (!name) continue;
    const k = keyOf(number, name);
    if (!seen.has(k)) seen.set(k, { name, number });
  }
  return [...seen.values()];
}

const HIT = new Set(['1B', '2B', '3B', 'HR']);
const NON_AB = new Set(['BB', 'HBP', 'SF', 'SAC']);

function rollUpBatting(atBats: AtBatDoc[]): PlayerStats {
  const pa = atBats.length;
  const ab = atBats.filter(a => !NON_AB.has(a.resultCode)).length;
  const h = atBats.filter(a => HIT.has(a.resultCode)).length;
  const bb = atBats.filter(a => a.resultCode === 'BB').length;
  const hbp = atBats.filter(a => a.resultCode === 'HBP').length;
  const so = atBats.filter(a => a.resultCode === 'K').length;
  const rbi = atBats.reduce((s, a) => s + (a.rbi || 0), 0);
  const sb = atBats.filter(a => /\bSB\b/.test(a.result)).length;
  const doubles = atBats.filter(a => a.resultCode === '2B').length;
  const triples = atBats.filter(a => a.resultCode === '3B').length;
  const hr = atBats.filter(a => a.resultCode === 'HR').length;
  const xbh = doubles + triples + hr;
  const sf = atBats.filter(a => a.resultCode === 'SF').length;
  const tb = h + doubles + 2 * triples + 3 * hr; // total bases (single=1 already in h)
  const obpDen = ab + bb + hbp + sf;
  const avg = ab > 0 ? h / ab : 0;
  const obp = obpDen > 0 ? (h + bb + hbp) / obpDen : 0;
  const slg = ab > 0 ? tb / ab : 0;
  return { pa, ab, h, bb, hbp, so, rbi, sb, xbh, avg, obp, slg, ops: obp + slg };
}

export function reconcileGameImport(input: ReconcileInput): ReconciledGame {
  const warnings: string[] = [];
  const out: ReconciledGame = {
    atBats: [], playerGameStats: {}, pitchCounts: {}, pitcherLines: {}, pitcherRates: {}, opponentRecord: null, unmatched: [], warnings,
  };
  if (!input.scorebook) { warnings.push('No scorebook provided.'); return out; }

  const book = input.scorebook;
  const { our, opp } = identifyOurPage(book, input.coachTeamName, input.roster, warnings);
  if (!our) { warnings.push('Could not identify the coach’s team page.'); return out; }

  out.date = book.date;
  out.ourTeamName = our.teamName;
  out.homeOrAway = our.side;
  out.opponent = opp?.teamName;
  out.opponentKey = opp ? opponentKeyOf(opp.teamName) : undefined;

  // Resolve our batters (our page) and our pitchers (they appear on the opponent page).
  const ourBatterMap = resolveToRoster(uniquePeople(our.atBats, 'batter'), input.roster, input.overrides);
  const ourPitcherMap = opp ? resolveToRoster(uniquePeople(opp.atBats, 'pitcher'), input.roster, input.overrides) : new Map<string, RosterRef>();

  // ── Our batters → AtBatDocs + per-game batting rollup ────────────────────────
  const byBatter = new Map<string, AtBatDoc[]>();
  for (const ab of our.atBats) {
    const matched = ourBatterMap.get(keyOf(ab.batterNumber, ab.batterName));
    const doc = toAtBatDoc(ab, 'us', matched?.id, 'opp', undefined, out.opponentKey);
    if (matched) doc.batterName = matched.name;   // adopt the roster's canonical name
    out.atBats.push(doc);
    if (matched) {
      (byBatter.get(matched.id) ?? byBatter.set(matched.id, []).get(matched.id)!).push(doc);
    } else {
      out.unmatched.push({ name: ab.batterName, number: ab.batterNumber, role: 'batter' });
    }
  }
  for (const [pid, abs] of byBatter) out.playerGameStats[pid] = rollUpBatting(abs);

  // ── Opponent batters (faced our pitchers) → AtBatDocs + our pitch counts ──────
  if (opp) {
    for (const ab of opp.atBats) {
      const matchedPitcher = ab.pitcherName ? ourPitcherMap.get(keyOf(ab.pitcherNumber || '', ab.pitcherName)) : undefined;
      const doc = toAtBatDoc(ab, 'opp', undefined, 'us', matchedPitcher?.id, out.opponentKey);
      if (matchedPitcher) doc.pitcherName = matchedPitcher.name; // canonical roster name
      out.atBats.push(doc);
      if (matchedPitcher) {
        out.pitchCounts[matchedPitcher.id] = (out.pitchCounts[matchedPitcher.id] || 0) + doc.pitchCount;
      } else if (ab.pitcherName) {
        // a pitcher we couldn't match to the roster — surface once
        if (!out.unmatched.some(u => u.role === 'pitcher' && u.number === (ab.pitcherNumber || '')))
          out.unmatched.push({ name: ab.pitcherName, number: ab.pitcherNumber || '', role: 'pitcher' });
      }
    }
    out.opponentRecord = buildOpponentRecord(opp, out.opponentKey!, book.date);
  }

  // ── Source precedence: the Game Stats Sheet's exact per-pitcher BF + P-S let us
  //    attribute the opponent at-bats to the precise pitcher (even mid-inning) and
  //    report exact pitch counts; batting totals override the scorebook rollup. ──
  if (input.gameStatsSheet?.teams?.length) {
    applyStatsSheet(input.gameStatsSheet, input.coachTeamName, input.roster, out, warnings);
  }

  // ── Third source: the game-filtered CSV carries the rate stats neither PDF has. ──
  if (input.gameCsv?.length) {
    applyGameCsv(input.gameCsv, input.roster, out, warnings);
  }

  return out;
}

const num = (v: number | null | undefined): number | undefined =>
  v == null || Number.isNaN(v) ? undefined : v;

/**
 * Fold in the GameChanger CSV, which is the only source carrying rate stats
 * (first-pitch strike, swinging-miss, two-strike outcomes, batted-ball quality).
 *
 * GC exports season-to-date by default; a coach is expected to filter to the one
 * game. We verify rather than trust: a pitcher's CSV row is accepted only when its
 * IP/BF/pitches agree with that pitcher's box-score line. A season export would
 * show larger totals and is rejected, because narrating season rates as tonight's
 * would be wrong in a way no one downstream could detect.
 */
function applyGameCsv(csv: ParsedGCPlayer[], roster: RosterRef[], out: ReconciledGame, warnings: string[]) {
  const matches = matchPlayers(
    csv.map(c => ({ last: c.last, first: c.first, number: c.number, stats: c.stats })),
    roster.map(r => ({ id: r.id, name: r.name, number: r.number })),
  );

  let applied = 0;
  let unverified = 0;
  const mismatched: string[] = [];

  matches.forEach((m, i) => {
    const pid = m.matchedPlayerId;
    const s = csv[i]?.stats;
    if (!pid || !s || !(s.ip && s.ip > 0)) return;

    // Cross-check against the box score before trusting these as THIS game's rates.
    const line = out.pitcherLines[pid];
    if (line) {
      const ipOff = Math.abs((s.ip ?? 0) - (line.ip ?? 0)) > 0.35;   // .1/.2 = thirds of an inning
      const bfOff = line.bf != null && s.bf != null && s.bf !== line.bf;
      const pOff  = line.pitches != null && s.pitches != null && s.pitches !== line.pitches;
      if (ipOff || bfOff || pOff) {
        mismatched.push(m.matchedPlayerName || csv[i].last);
        return;
      }
    } else {
      // No box-score line to check against (the coach supplied only the scorebook
      // and the CSV), so we cannot tell a game export from a season one here.
      unverified++;
    }

    out.pitcherRates[pid] = {
      fpsPct:      num(s.fpsPct),
      fpsoPct:     num(s.fpsoPct),
      fpswPct:     num(s.fpswPct),
      fpshPct:     num(s.fpshPct),
      smPct:       num(s.smPct),
      kBf:         num(s.kBf),
      loo:         num(s.loo),
      lowPitchInn: num(s.lowPitchInn),
      weakPct:     num(s.weakPct),
      goAo:        num(s.goAo),
      baa:         num(s.baa),
    };
    applied++;
  });

  if (mismatched.length) {
    warnings.push(`CSV totals didn't match the box score for ${mismatched.join(', ')}. Looks like a season export rather than this game, so its rates were skipped. In GameChanger, filter the export to this game.`);
  }
  if (applied === 0 && !mismatched.length) {
    warnings.push('CSV had no pitching rows matching your roster. No rate stats added.');
  }
  if (unverified > 0 && applied > 0) {
    warnings.push('CSV rates were used without a box score to check them against. Add the Game Stats Sheet PDF if you want them verified against this game.');
  }
}

function toAtBatDoc(
  ab: ParsedAtBat,
  batterTeam: 'us' | 'opp', batterId: string | undefined,
  pitcherTeam: 'us' | 'opp', pitcherId: string | undefined,
  opponentKey: string | undefined,
): AtBatDoc {
  return {
    gameId: '',                      // filled by the persistence layer
    inning: ab.inning,
    batterTeam,
    batterId,
    batterName: ab.batterName,
    batterNumber: ab.batterNumber,
    lineupSlot: ab.lineupSlot,
    timeThroughOrder: ab.timeThroughOrder,
    pitcherTeam,
    pitcherId,
    pitcherName: ab.pitcherName,
    pitcherNumber: ab.pitcherNumber,
    opponentKey,
    result: ab.result,
    resultCode: ab.resultCode,
    resultDescription: ab.resultDescription,
    reachedBase: ab.reachedBase,
    rbi: ab.rbi,
    runnersScored: ab.runnersScored,
    isLeadoff: ab.isLeadoff,
    pitchSequence: ab.pitchSequence,
    balls: ab.balls,
    strikes: ab.strikes,
    pitchCount: ab.pitchCount,
    confidence: ab.confidence,
  };
}

function buildOpponentRecord(opp: ParsedTeamPage, key: string, date: string | undefined): OpponentRecord {
  const players: OpponentRecord['players'] = {};
  for (const ab of opp.atBats) {
    const num = String(ab.batterNumber).trim();
    if (!num) continue;
    const rec = players[num] ?? (players[num] = { number: num, name: ab.batterName, slotsSeen: [] });
    if (!rec.slotsSeen.includes(ab.lineupSlot)) rec.slotsSeen.push(ab.lineupSlot);
  }
  const d = date || new Date().toISOString().split('T')[0];
  return { key, name: opp.teamName, firstSeen: d, lastSeen: d, gameIds: [], players };
}

const lastNm = (n: string) => ((n || '').trim().split(/\s+/).pop() || '').toLowerCase();

/**
 * The Game Stats Sheet carries exact per-pitcher BF + P-S. Because the scorebook
 * gives the opponent at-bats in chronological order, we slice that ordered list by
 * each pitcher's BF (in pitching order) to attribute every at-bat to the precise
 * pitcher — even when changes happened mid-inning. Pitch counts come from P-S
 * (TOTAL−STRIKES). Batting totals override the scorebook rollup.
 */
function applyStatsSheet(sheet: ParsedGameStatsSheet, coachTeamName: string, roster: RosterRef[], out: ReconciledGame, warnings: string[]) {
  const target = norm(coachTeamName);
  let ours = sheet.teams.find(t => { const n = norm(t.teamName); return target && (n.includes(target) || target.includes(n)); });

  // Fall back to roster jersey overlap, exactly as identifyOurPage does for the
  // scorebook. The team's name in Dugout rarely matches GameChanger's name for it
  // ("KC Pelicans" vs "Pelicans 10u" share no substring), and a name-only match
  // silently threw the whole box score away: every pitch count reverted to the
  // scorebook's estimate, and any pitcher the scorebook could not see disappeared.
  // The roster is the reliable signal — these are the coach's own players.
  if (!ours) {
    const rosterNums = new Set(roster.map(r => String(r.number).trim()));
    let best = 0;
    for (const t of sheet.teams) {
      const overlap = t.batters.filter(b => rosterNums.has(String(b.number).trim())).length;
      if (overlap > best) { best = overlap; ours = t; }
    }
    if (ours && best > 0) {
      warnings.push(`Stats sheet: team name "${coachTeamName}" didn't match "${ours.teamName}", matched by roster jersey overlap (${best}).`);
    } else {
      ours = undefined;
    }
  }

  if (!ours) { warnings.push('Stats sheet: could not match the coach’s team, skipped.'); return; }
  if (sheet.date && !out.date) out.date = sheet.date;

  const byNum = (num: string) => roster.find(r => String(r.number).trim() === String(num).trim());
  const byName = (nm: string) => nm ? roster.find(r => lastNm(r.name) === lastNm(nm)) : undefined;
  const resolve = (num: string, nm: string) => byNum(num) || byName(nm);

  // Exact pitcher re-attribution: slice chronological opponent at-bats by BF.
  const oppAtBats = out.atBats.filter(ab => ab.pitcherTeam === 'us');
  const totalBf = ours.pitchers.reduce((s, p) => s + (p.bf || 0), 0);

  // The stats sheet is the system of record for WHO pitched and for their totals,
  // so its pitcher list is always applied. Only the at-bat-level re-attribution
  // below needs the counts to line up.
  //
  // This used to live inside the exact-match branch, which meant a single
  // miscounted at-bat in the scorebook grid (a mis-banded row, or an overflow
  // page re-rendering a plate appearance) dropped the whole team back to
  // scorebook-only attribution. A pitcher the scorebook never identified — a
  // reliever whose move to the mound isn't recorded in the roster grid's
  // position-change columns, which is the norm for a fielder who comes in to
  // pitch — then vanished from the import entirely, taking their pitch count and
  // arm-health record with them.
  out.pitchCounts = {};
  for (const p of ours.pitchers) {
    const rp = resolve(p.number, p.name);
    if (!rp) continue;
    if (p.pitches != null) out.pitchCounts[rp.id] = p.pitches;
    // Carry the roster's canonical name: the stats sheet truncates to fit its
    // column ("G Jaco" for Jacobsen), and this line is what names the pitcher
    // on any card built without play-by-play to fall back on.
    out.pitcherLines[rp.id] = { ...p, name: rp.name || p.name };
  }

  if (ours.pitchers.length && ours.pitchers.every(p => p.bf != null) && totalBf === oppAtBats.length) {
    // Counts reconcile: slice the chronological opponent at-bats by BF to pin every
    // at-bat to the precise pitcher, even for a mid-inning change.
    let idx = 0;
    for (const p of ours.pitchers) {
      const rp = resolve(p.number, p.name);
      // Best name: roster canonical > stats sheet parsed > existing at-bat name.
      const resolvedName = rp?.name || p.name || '';
      for (let k = 0; k < (p.bf || 0); k++) {
        const ab = oppAtBats[idx++];
        if (!ab) break;
        // Only overwrite if we have a real name — keep the scorebook-derived name as fallback.
        if (resolvedName) ab.pitcherName = resolvedName;
        ab.pitcherNumber = p.number;
        ab.pitcherId = rp?.id;
      }
    }
  } else {
    warnings.push(`Stats sheet BF total (${totalBf}) ≠ scorebook opponent at-bats (${oppAtBats.length}); per-at-bat pitcher attribution kept from the scorebook, but per-pitcher totals come from the stats sheet.`);
  }

  // Batting totals (authoritative). Persisted only when game.stats integration is on.
  for (const b of ours.batters) {
    const rp = resolve(b.number, b.name);
    if (!rp) continue;
    const obpDen = b.ab + b.bb;
    out.playerGameStats[rp.id] = {
      ...(out.playerGameStats[rp.id] || {}),
      pa: b.ab + b.bb, ab: b.ab, h: b.h, bb: b.bb, so: b.so, rbi: b.rbi, r: b.r,
      avg: b.ab > 0 ? b.h / b.ab : 0,
      obp: obpDen > 0 ? (b.h + b.bb) / obpDen : 0,
    };
  }
}
